const waitlist = { label: "Join the waitlist", action: "modal", modalKind: "waitlist" };
const contact = { label: "Talk to us", action: "modal", modalKind: "contact" };
const seeDemo = { label: "See it in action", href: "/demo", external: false };
const seeCompanion = { label: "See the AI Companion", href: "/products/companion", external: false };
const seeMoodCoach = { label: "See the Mood Coach", href: "/products/mood-coach", external: false };
const seeHumanizer = { label: "See Text Humanizer", href: "/products/text-humanizer", external: false };
const seeVoiceJournal = { label: "See Voice Journal", href: "/products/voice-journal", external: false };
const seeCustomTraining = { label: "See Custom Model Training", href: "/products/custom-training", external: false };
const seeVoicePlay = { label: "Explore VoicePlay Engine", href: "https://voiceplay.aihumanity.io", external: true };
const seeSessionInsights = { label: "See Session Insights", href: "/products/session-insights", external: false };
const seeVoiceAgentDemo = { label: "Try the live demo", href: "/demo/voice-agent", external: false };
const seeVoiceHostDemo = { label: "See the voice front desk demo", href: "/demo/voice-host", external: false };

export const USE_CASE_CONFIGS = {
    "companionship": {
        slug: "companionship",
        segment: "b2c",
        eyebrow: "Use Case",
        title: "Reduce Loneliness",
        tagline: "Feel Less Alone, Without Faking a Friendship",
        description: "Most chat AI resets every conversation to zero. It can't tell you're having a rough week, and it won't remember that you were having one yesterday. A companion that keeps context turns small daily check-ins into something that actually feels like being known.",
        seoDescription: "How persistent-memory AI companionship reduces loneliness by remembering context across conversations instead of resetting every session.",
        stats: [
            { val: "24/7", label: "available presence" },
            { val: "Persistent", label: "memory across sessions" },
        ],
        primaryCta: waitlist,
        secondaryCta: seeCompanion,
        featuresHeading: "Why memory changes everything",
        featuresIntro: "Continuity is the difference between a chatbot and a companion.",
        features: [
            {
                tag: "Continuity",
                title: "It remembers your week, not just this message",
                description: "Past conversations inform the next one, so you're not re-explaining your life every time you open the app.",
                bullets: ["Recalls prior topics and moods", "Follows up on things you mentioned", "No cold-start every session"],
            },
            {
                tag: "Emotional attunement",
                title: "Responds to how you feel, not just what you typed",
                description: "The same words land differently depending on tone and context — the companion adjusts pacing and warmth accordingly.",
                bullets: ["Reads frustration, fatigue, and calm differently", "Adjusts tone instead of using one script", "Avoids tone-deaf responses to hard moments"],
            },
        ],
        useCasesHeading: "Powered by",
        useCases: [
            { icon: "💬", title: "AI Companion", body: "Adaptive chat that remembers you across sessions.", href: "/products/companion", external: false },
            { icon: "📈", title: "Emotion Profile Tracking", body: "See how your mood shifts over weeks, not just one chat.", href: "/products/emotion-profile", external: false },
        ],
        ctaHeading: "A companion that actually keeps up with your life",
        ctaBody: "Join the waitlist to try persistent-memory companionship for yourself.",
        ctaButton: waitlist,
    },

    "mood-awareness": {
        slug: "mood-awareness",
        segment: "b2c",
        eyebrow: "Use Case",
        title: "Build Mood Awareness",
        tagline: "See Your Patterns Before They Become a Crisis",
        description: "Most people only notice a bad stretch after it's already bad. Tracking emotional signal over time — not just a single mood check-in — surfaces the slow drift long before it turns into a spiral, and pairs it with guidance instead of just a chart.",
        seoDescription: "Use emotion tracking and an AI mood coach to spot emotional patterns early and get guided self-help before small dips become bigger problems.",
        stats: [
            { val: "Trend-based", label: "not single-point mood logging" },
            { val: "Guided", label: "coaching on what to do next" },
        ],
        primaryCta: waitlist,
        secondaryCta: seeMoodCoach,
        featuresHeading: "From noticing to doing something about it",
        featuresIntro: "Awareness alone isn't enough — pattern detection has to lead somewhere.",
        features: [
            {
                tag: "Pattern detection",
                title: "Surfaces drift, not just daily snapshots",
                description: "A single mood log tells you how today felt. Weeks of signal tell you whether you're trending somewhere that needs attention.",
                bullets: ["Tracks mood trends across weeks", "Flags gradual shifts, not just spikes", "Private by default"],
            },
            {
                tag: "Guided response",
                title: "Turns a pattern into a next step",
                description: "Instead of a dashboard you have to interpret yourself, guided coaching translates the pattern into something actionable.",
                bullets: ["Suggested check-ins based on your trend", "Grounded coaching techniques, not generic tips", "Escalation prompts when patterns look concerning"],
            },
        ],
        useCasesHeading: "Powered by",
        useCases: [
            { icon: "📈", title: "Emotion Profile Tracking", body: "Your mood patterns over time, not just a single check-in.", href: "/products/emotion-profile", external: false },
            { icon: "🧭", title: "Self-Help & Mood Coach", body: "Guided emotional well-being based on what your trend shows.", href: "/products/mood-coach", external: false },
        ],
        ctaHeading: "Catch the pattern before it catches you",
        ctaBody: "Join the waitlist for early access to emotion tracking and guided coaching.",
        ctaButton: waitlist,
    },

    "authentic-writing": {
        slug: "authentic-writing",
        segment: "b2c",
        eyebrow: "Use Case",
        title: "Write Like Yourself",
        tagline: "Sound Like Yourself Online, Not Like a Bot",
        description: "AI drafting tools are fast, but the output reads stiff, generic, and instantly recognizable as machine-written — which costs trust with readers and triggers AI-detection flags. Humanizing the draft keeps your voice and your meaning while removing the tells.",
        seoDescription: "Humanize AI-generated writing to remove robotic phrasing and detection tells while keeping your voice, for articles, emails, and messages.",
        stats: [
            { val: "+61%", label: "brand warmth" },
            { val: "-48%", label: "AI stiffness" },
        ],
        statsNote: "From internal side-by-side testing on sample drafts, not an independent audit.",
        primaryCta: waitlist,
        secondaryCta: seeHumanizer,
        featuresHeading: "What actually changes in the text",
        featuresIntro: "Not just synonym-swapping — a rewrite that preserves meaning and voice.",
        features: [
            {
                tag: "Voice preservation",
                title: "Keeps your point, changes the delivery",
                description: "The humanizer rewrites structure and phrasing while leaving your actual argument and facts untouched.",
                bullets: ["No meaning drift from the original draft", "Removes repetitive AI sentence patterns", "Works on articles, emails, and messages"],
            },
            {
                tag: "Readability",
                title: "Reads like a person wrote it, because it still needed one",
                description: "Small imperfections and natural rhythm replace the overly uniform cadence that gives AI writing away.",
                bullets: ["+37% readability in side-by-side tests", "Fewer flagged passages in AI-detection tools", "Tone options from casual to professional"],
            },
            {
                tag: "Before / after",
                title: "\"We are pleased to announce...\" becomes \"Good news —...\"",
                description: "A same-paragraph example of what changes: the facts and structure stay put, the delivery doesn't sound like a template.",
                bullets: ["Before: \"We are pleased to announce our Q3 results...\"", "After: \"Here's how Q3 went...\"", "Same meaning, same facts, different voice"],
            },
        ],
        useCasesHeading: "Powered by",
        useCases: [
            { icon: "✍️", title: "Text Humanizer", body: "Un-AI your articles and messages before you hit send.", href: "/products/text-humanizer", external: false },
        ],
        ctaHeading: "Keep the speed of AI drafting, lose the robotic voice",
        ctaBody: "Join the waitlist to try the Text Humanizer on your own writing.",
        ctaButton: waitlist,
    },

    "daily-reflection": {
        slug: "daily-reflection",
        segment: "b2c",
        eyebrow: "Use Case",
        title: "Make Sense of Your Day",
        tagline: "Turn Rambling Thoughts Into Real Insight",
        description: "Talking through your day out loud is easier than writing it down — but a voice memo you never replay isn't reflection, it's just noise you're storing. Turning spoken venting into structured insight is what makes the habit worth keeping.",
        seoDescription: "Speak instead of write to reflect on your day, and get structured emotional insight back instead of an unplayed voice memo.",
        stats: [
            { val: "Spoken", label: "input, no typing required" },
            { val: "Structured", label: "insight back, not a raw transcript" },
        ],
        primaryCta: waitlist,
        secondaryCta: seeVoiceJournal,
        featuresHeading: "From venting to actual insight",
        featuresIntro: "The value isn't the recording — it's what comes back after it.",
        features: [
            {
                tag: "Low friction",
                title: "Speak it instead of writing it",
                description: "Voice removes the blank-page problem that kills most journaling habits before they start.",
                bullets: ["No typing, just talk", "Works for two minutes or twenty", "Private by default"],
            },
            {
                tag: "Structured reflection",
                title: "Comes back as insight, not just a transcript",
                description: "Tone and word choice are analyzed to surface what actually mattered in what you said, not just what you said.",
                bullets: ["Highlights recurring themes across entries", "Surfaces the emotional throughline, not just topics", "Tracks change over weeks, not single entries"],
            },
            {
                tag: "Sample output",
                title: "A two-minute ramble becomes three lines you'd actually reread",
                description: "An illustrative example of what a raw entry turns into once it's structured — not a raw transcript dump.",
                bullets: ["Transcript: unedited, exactly what you said", "Insight: the theme and emotional throughline underneath it", "Trend: how today compares to the rest of the week"],
            },
        ],
        useCasesHeading: "Powered by",
        useCases: [
            { icon: "🎙️", title: "Voice Journal", body: "Speak it, get structured insight back.", href: "/products/voice-journal", external: false },
        ],
        ctaHeading: "Reflection that doesn't require a blank page",
        ctaBody: "Join the waitlist to try voice journaling with real insight, not just a recording.",
        ctaButton: waitlist,
    },

    "support-de-escalation": {
        slug: "support-de-escalation",
        segment: "b2b",
        eyebrow: "Use Case",
        title: "Reduce Support Escalations",
        tagline: "Cut Support Escalations Before They Start",
        description: "The same scripted response to a calm question and a furious one is how tickets turn into escalations. Reading frustration, confusion, or distress in real time and adapting tone and strategy on the spot is what keeps a hard moment from becoming a bad review.",
        seoDescription: "Use real-time emotion recognition in support and agent workflows to detect frustration early and reduce escalation risk.",
        stats: [
            { val: "-32%", label: "escalation risk (billing scenario)" },
            { val: "+54%", label: "trust lift (policy scenario)" },
        ],
        statsNote: "Measured from the Agent EQ demo scenarios below — run them yourself to see the comparison.",
        primaryCta: contact,
        secondaryCta: seeDemo,
        featuresHeading: "What changes in the interaction",
        featuresIntro: "Try it live: the Demo Lab's Agent EQ tab runs this exact comparison.",
        features: [
            {
                tag: "Real-time detection",
                title: "Reads frustration before it escalates",
                description: "Voice tone, word choice, and pacing are scored live so a rising-frustration moment gets flagged while there's still time to change course.",
                bullets: ["Frustration, confusion, and distress scored live", "Works across voice and text channels", "No manual tagging required — scored turn by turn"],
            },
            {
                tag: "Adaptive response",
                title: "Shifts strategy, not just tone of voice",
                description: "Detected emotion changes what the agent (human or AI) does next — ownership language, pacing, and prioritization all adapt.",
                bullets: ["Repair-mode responses for high-frustration moments", "Guided pacing for confused or new users", "Care-first framing for distressed customers"],
            },
        ],
        useCasesHeading: "Powered by",
        useCases: [
            { icon: "🧠", title: "Emotion Models", body: "Face · voice · text recognition, tuned for support conversations.", href: "/products/emotion-models", external: false },
            { icon: "🔗", title: "Multimodal Fusion", body: "Combine voice and text signal for a more reliable read.", href: "/products/fusion", external: false },
            { icon: "⚙️", title: "Emotion API & SDK", body: "REST and client SDKs to wire this into your existing stack.", href: "https://developer.aihumanity.io", external: true },
            { icon: "📊", title: "Session Insights", body: "Aggregate every call into a post-session report for coaching and QA.", href: "/products/session-insights", external: false },
        ],
        ctaHeading: "See the before/after on real support scenarios",
        ctaBody: "Try the live Agent EQ demo, then talk to us about integrating it into your support stack.",
        ctaButton: seeDemo,
    },

    "interaction-insights": {
        slug: "interaction-insights",
        segment: "b2b",
        eyebrow: "Use Case",
        title: "Turn Every Interaction Into Insight",
        tagline: "See How Every Customer Actually Felt — Not Just What They Said",
        description: "Live emotion detection changes what happens in the moment. But most support calls, interviews, and usability tests still end with nothing more than a transcript and a CSAT score. A structured emotion report — generated automatically after the session — surfaces which moments actually mattered, without a QA lead re-listening to every call.",
        seoDescription: "Turn recorded support calls, user interviews, and usability tests into structured post-session emotion reports for coaching, QA, and research.",
        stats: [
            { val: "Post-session", label: "structured report, not a raw recording" },
            { val: "Automatic", label: "no manual review or tagging" },
        ],
        primaryCta: contact,
        secondaryCta: seeSessionInsights,
        featuresHeading: "What you get after the session ends",
        featuresIntro: "Try it yourself: every call in the live Voice Agent demo ends with exactly this kind of report.",
        features: [
            {
                tag: "Emotional arc",
                title: "How the session opened, moved, and ended",
                description: "Not a single sentiment score — a timeline of how the person's emotional state moved across the whole interaction.",
                bullets: ["Dominant emotion with a confidence score", "Opening vs. closing state, side by side", "Flags for moments that needed a strategy change"],
            },
            {
                tag: "No manual review",
                title: "Skips the re-listen",
                description: "QA leads and researchers don't have to re-listen to every call to find the ones that went sideways — the report flags them.",
                bullets: ["Automatic key-moment detection", "Works across voice, video, or text sessions", "Aggregate trends across many sessions, not just one"],
            },
        ],
        useCasesHeading: "Powered by",
        useCases: [
            { icon: "📊", title: "Session Insights", body: "Structured post-session emotion reports for any recorded interaction.", href: "/products/session-insights", external: false },
            { icon: "🧠", title: "Emotion Models", body: "Face · voice · text recognition behind every report.", href: "/products/emotion-models", external: false },
            { icon: "🔗", title: "Multimodal Fusion", body: "Combines every signal captured during the session.", href: "/products/fusion", external: false },
        ],
        ctaHeading: "See a real session report",
        ctaBody: "Run the live voice agent demo end to end, then talk to us about wiring this into your own support, research, or CX workflow.",
        ctaButton: seeVoiceAgentDemo,
    },

    "ai-front-desk-for-homestays": {
        slug: "ai-front-desk-for-homestays",
        segment: "b2b",
        eyebrow: "Use Case",
        title: "AI Front Desk For Homestays",
        tagline: "Never Leave Guests Alone At The Door",
        description: "A WeChat-first, voice-enabled AI front desk for homestays and serviced apartments. It takes over post-booking guest support from check-in through checkout, handling arrival guidance, access issues, stay questions, and escalations without requiring a full front-desk team.",
        seoDescription: "AIHumanity AI Front Desk for homestays and serviced apartments — a WeChat-first, voice-enabled guest-service layer for check-in, in-stay support, issue triage, and service intelligence.",
        stats: [
            { val: "24/7", label: "post-booking guest coverage" },
            { val: "Voice + WeChat", label: "guest interaction layer" },
            { val: "Service Intelligence", label: "QA + profiling layer" },
        ],
        primaryCta: contact,
        secondaryCta: seeVoiceHostDemo,
        proof: {
            eyebrow: "Why Now",
            title: "China hospitality and rental-stay operators already have demand. What they lack is a property-level AI front desk after booking.",
            body: "The wedge is post-booking operations, not generic travel planning. AIHumanity fits here by combining real-time voice interaction with service-quality intelligence.",
            items: [
                { label: "China domestic trips (2025)", value: "6.522B", detail: "Large base of lodging demand without needing to own discovery." },
                { label: "Q1 2026 domestic trip growth", value: "+6.0%", detail: "Demand still growing, with more pressure on operators to improve service efficiency." },
                { label: "Tujia 15 / 30 day stay growth", value: "40%+", detail: "Longer stays create more service questions, exceptions, and extension opportunities." },
                { label: "Meituan merchant AI resolution", value: "95%", detail: "The market already accepts AI in adjacent merchant-service workflows." },
            ],
            preview: {
                eyebrow: "Product Split",
                title: "Interaction layer + intelligence layer",
                language: "Positioning",
                code: [
                    "AI Front Desk",
                    "- WeChat-first guest service",
                    "- real-time voice + text interaction",
                    "- check-in, in-stay support, escalation",
                    "",
                    "Service Intelligence",
                    "- guest-state profiling",
                    "- service-quality feedback",
                    "- issue pattern detection",
                    "- operator QA and coaching",
                ].join("\n"),
            },
        },
        featuresHeading: "What the operator actually gets",
        featuresIntro: "This is not just a chatbot. It is a guest-facing front desk plus an operator-facing intelligence layer.",
        features: [
            {
                tag: "Guest interaction",
                title: "A voice-first AI front desk from check-in through checkout",
                description: "Guests can use WeChat to start a voice-guided support flow for arrival, access, Wi-Fi, appliances, parking, local guidance, and common in-stay issues.",
                bullets: ["WeChat-first entry point", "H5/web voice fallback for richer UI", "Human takeover when AI cannot resolve the issue"],
            },
            {
                tag: "Operations",
                title: "Turns guest issues into routed actions",
                description: "Instead of burying problems inside voice calls or message threads, the system turns failed check-ins, cleaning issues, and maintenance requests into structured operator actions.",
                bullets: ["Repair and cleaning triage", "Escalation summaries for operators", "Operator timeline across voice and text"],
            },
            {
                tag: "Intelligence",
                title: "Profiles interaction quality across voice and text",
                description: "The service-intelligence layer scores guest confusion, frustration, and quality risk so operators can see where properties, check-in flows, or support practices are failing.",
                bullets: ["Session-level service-quality feedback", "Frustration and confusion markers", "Property-level issue pattern detection"],
            },
        ],
        useCasesHeading: "Powered by",
        useCases: [
            { icon: "🎙️", title: "Voice Front Desk Demo", body: "A rental-stay bridge page before the generic live voice demo.", href: "/demo/voice-host", external: false },
            { icon: "📊", title: "Session Insights", body: "Post-session reporting and service-quality feedback for every interaction.", href: "/products/session-insights", external: false },
            { icon: "🧠", title: "Emotion Models", body: "Voice and text signal used to detect frustration, confusion, and escalation risk.", href: "/products/emotion-models", external: false },
            { icon: "🔗", title: "Multimodal Fusion", body: "Combines interaction signals into a more reliable service-intelligence layer.", href: "/products/fusion", external: false },
        ],
        ctaHeading: "See how an AI front desk fits your properties",
        ctaBody: "Talk to us about a homestay or serviced-apartment pilot, or start with the live voice demo to see the interaction layer in action.",
        ctaButton: contact,
    },

    "player-retention": {
        slug: "player-retention",
        segment: "b2b",
        eyebrow: "Use Case",
        title: "Increase Player Retention",
        tagline: "NPCs Players Actually Remember",
        description: "Generic NPC dialogue trees get skipped after the first playthrough. NPCs that read player tone and react — remembering prior choices and adjusting delivery — give players a reason to actually engage with characters instead of clicking past them.",
        seoDescription: "Emotion-aware NPC dialogue and voice for game studios to increase player engagement and retention beyond static dialogue trees.",
        stats: [
            { val: "Reactive", label: "NPC dialogue, not static trees" },
            { val: "Persistent", label: "character memory across sessions" },
        ],
        primaryCta: contact,
        secondaryCta: seeVoicePlay,
        featuresHeading: "Why reactive NPCs hold attention longer",
        featuresIntro: "Built for studios shipping character-driven experiences.",
        features: [
            {
                tag: "Character runtime",
                title: "Drop-in emotional reactivity for NPCs",
                description: "A runtime layer that reads player voice/text tone and adjusts NPC delivery without rewriting your dialogue system.",
                bullets: ["Works with existing dialogue trees", "Tunable per-character emotional range", "Low-latency, built for real-time gameplay"],
            },
            {
                tag: "Memory",
                title: "NPCs that reference what happened last session",
                description: "Persistent state means a character can call back to a prior choice or conversation instead of resetting every encounter.",
                bullets: ["Cross-session character memory", "Reacts differently based on player history", "Configurable memory depth per NPC"],
            },
        ],
        useCasesHeading: "Powered by",
        useCases: [
            { icon: "🎮", title: "VoicePlay Engine", body: "Game NPC character runtime with live emotional reactivity.", href: "https://voiceplay.aihumanity.io", external: true },
            { icon: "🧠", title: "Emotion Models", body: "Face · voice · text recognition tuned for game audio.", href: "/products/emotion-models", external: false },
        ],
        ctaHeading: "Give your characters a reason to be remembered",
        ctaBody: "Talk to us about integrating VoicePlay into your title.",
        ctaButton: contact,
    },

    "onboarding-drop-off": {
        slug: "onboarding-drop-off",
        segment: "b2b",
        eyebrow: "Use Case",
        title: "Reduce Onboarding Drop-off",
        tagline: "Stop Losing Users in the First Five Minutes",
        description: "Most onboarding flows treat a confused first-time user the same as a confident returning one. Detecting confusion or hesitation in the moment — and switching to a guided, slower-paced flow — keeps new users past the point where they'd normally give up.",
        seoDescription: "Detect confusion and hesitation in real time during onboarding to reduce first-session drop-off with emotion-aware guided flows.",
        stats: [
            { val: "-33%", label: "onboarding drop-off" },
            { val: "+41%", label: "trust lift for new users" },
        ],
        statsNote: "Measured from the Agent EQ demo scenarios below — run them yourself to see the comparison.",
        primaryCta: contact,
        secondaryCta: seeDemo,
        featuresHeading: "What changes for a first-time user",
        featuresIntro: "Try it live: the Demo Lab's Agent EQ tab includes this exact scenario.",
        features: [
            {
                tag: "Confusion detection",
                title: "Notices hesitation before the user bounces",
                description: "Tone and interaction patterns signal confusion well before a user opens a support ticket or just leaves.",
                bullets: ["Reads confusion and anxiety signals live", "No explicit user feedback required", "Triggers before drop-off, not after"],
            },
            {
                tag: "Guided fallback",
                title: "Switches to a slower, guided path automatically",
                description: "Instead of repeating the same instructions, the flow adapts pacing and offers to walk through the next step directly.",
                bullets: ["Step-by-step guided mode for confused users", "Normal-speed flow stays untouched for confident users", "Reduces repeat-contact rate for the same issue"],
            },
        ],
        useCasesHeading: "Powered by",
        useCases: [
            { icon: "👁️", title: "AI Perception API", body: "Heartbeat, gaze, and calmness signal for engagement detection.", href: "/perception", external: false },
            { icon: "⚙️", title: "Emotion API & SDK", body: "REST and client SDKs to wire detection into your onboarding flow.", href: "https://developer.aihumanity.io", external: true },
        ],
        ctaHeading: "See the onboarding scenario in the live demo",
        ctaBody: "Try the Agent EQ demo, then talk to us about your onboarding flow.",
        ctaButton: seeDemo,
    },

    "domain-tuned-models": {
        slug: "domain-tuned-models",
        segment: "b2b",
        eyebrow: "Use Case",
        title: "Improve Emotion Accuracy on Your Own Data",
        tagline: "Generic Emotion Models Miss Your Users",
        description: "Off-the-shelf emotion recognition is trained on generic data — it misjudges clinical patients, game characters, industry-specific speech patterns, and specialized sensor hardware. A model fine-tuned on your data reads your users correctly, without changing your integration.",
        seoDescription: "Fine-tune emotion recognition models on your own domain data for clinical, gaming, enterprise CX, or robotics use cases without changing your API integration.",
        stats: [
            { val: "Same API", label: "no integration changes" },
            { val: "Domain-tuned", label: "accuracy on your data" },
        ],
        primaryCta: contact,
        secondaryCta: seeCustomTraining,
        featuresHeading: "Same contract, better accuracy",
        featuresIntro: "Built for teams where generic emotion models fall short.",
        features: [
            {
                tag: "Drop-in upgrade",
                title: "Same API. Better numbers.",
                description: "The fine-tuned model keeps the same label space and API as the base model — your integration code doesn't change, only the accuracy on your domain does.",
                bullets: ["No SDK changes required", "Same calibration contract", "Side-by-side A/B harness to prove the lift"],
            },
            {
                tag: "Your data, your terms",
                title: "Trained on your consented data, not ours",
                description: "Model tuning runs on data you provide and control, with a scoping call up front to define what's in and out of bounds.",
                bullets: ["Consented data only", "Sample model card before commitment", "Scoping call to define data boundaries"],
            },
        ],
        useCasesHeading: "Where this applies",
        useCases: [
            { icon: "🏥", title: "Clinical research", body: "Domain-tuned models on consented patient data.", href: "/products/custom-training", external: false },
            { icon: "🎮", title: "Game studios", body: "Tune to your characters, your players, your genre.", href: "/products/custom-training", external: false },
            { icon: "🏢", title: "Enterprise CX", body: "Industry-specific emotional cues at scale.", href: "/products/custom-training", external: false },
            { icon: "🤖", title: "Robotics OEMs", body: "Optimized for your sensor hardware and form factor.", href: "/products/custom-training", external: false },
        ],
        ctaHeading: "Let's tune a model for your problem",
        ctaBody: "Send us a note. We'll schedule a scoping call and share a sample model card.",
        ctaButton: contact,
    },
};

export function getUseCaseConfig(slug) {
    return USE_CASE_CONFIGS[slug] || null;
}
